import { useState, useCallback } from 'react';
import { Search, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { taxApi } from '@/api/tax.api';
import type { TaxTransaction } from '@/api/tax.api';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

const SOURCE_MODULES = ['AR', 'AP', 'GL', 'BNK'];

const MODULE_COLORS: Record<string, string> = {
  AR: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400',
  AP: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  GL: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  BNK: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
};

const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtAmt  = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2 });

export const TaxInquiryPage = () => {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  const [dateFrom,    setDateFrom]    = useState(firstOfMonth);
  const [dateTo,      setDateTo]      = useState(today);
  const [module,      setModule]      = useState('');
  const [taxCodeId] = useState('');
  const [inputFilter, setInputFilter] = useState<'all' | 'input' | 'output'>('all');

  const [items,   setItems]   = useState<TaxTransaction[]>([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const pageSize = 30;

  const run = useCallback(async (p = 1) => {
    setLoading(true); setPage(p);
    try {
      const params: Record<string, unknown> = { pageNumber: p, pageSize };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo)   params.dateTo   = dateTo;
      if (module)   params.sourceModule = module;
      if (taxCodeId) params.taxCodeId = taxCodeId;
      if (inputFilter === 'input')  params.isInputTax = true;
      if (inputFilter === 'output') params.isInputTax = false;
      const res = await taxApi.getTransactions(params);
      const d = res.data?.data;
      setItems(d?.items ?? []);
      setTotal(d?.totalCount ?? 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, module, taxCodeId, inputFilter]);

  const totalPages = Math.ceil(total / pageSize);

  // Summary computations
  const totalTaxable     = items.reduce((s, r) => s + r.taxableAmount, 0);
  const totalTax         = items.reduce((s, r) => s + r.taxAmount, 0);
  const totalRecoverable = items.filter(r => r.isRecoverable).reduce((s, r) => s + r.taxAmount, 0);
  const totalNonRecov    = totalTax - totalRecoverable;

  const summaryCards = [
    { label: 'Total Taxable', value: fmtAmt(totalTaxable), color: 'text-slate-800 dark:text-gray-200' },
    { label: 'Total Tax',     value: fmtAmt(totalTax),     color: 'text-blue-700 dark:text-blue-400' },
    { label: 'Recoverable',   value: fmtAmt(totalRecoverable), color: 'text-green-700 dark:text-green-400' },
    { label: 'Non-Recoverable', value: fmtAmt(totalNonRecov), color: 'text-red-700 dark:text-red-400' },
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2 mb-4">
          <Search size={20} className="text-brand-600" /> Tax Inquiry
        </h1>
        {/* Filter bar */}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Date From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-brand-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Date To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-brand-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Source Module</label>
            <select value={module} onChange={e => setModule(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-brand-500 outline-none">
              <option value="">All Modules</option>
              {SOURCE_MODULES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Input / Output</label>
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden">
              {(['all', 'input', 'output'] as const).map(v => (
                <button key={v} onClick={() => setInputFilter(v)}
                  className={cn('px-3 py-1.5 text-xs font-medium transition-colors',
                    inputFilter === v ? 'bg-brand-600 text-white' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600')}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => run(1)} disabled={loading}
            className="h-9 px-5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center gap-1.5 transition-colors">
            {loading && <Spinner size="sm" />} Run
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {items.length > 0 && (
        <div className="grid grid-cols-4 gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          {summaryCards.map(({ label, value, color }) => (
            <div key={label} className="text-center py-2 px-4 rounded-lg bg-slate-50 dark:bg-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">{label}</p>
              <p className={cn('text-lg font-bold font-mono', color)}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <FileText size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">Set filters and click Run to load transactions</p>
          </div>
        )}
        {loading && (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                {['Date', 'Module', 'Doc No', 'Trans Type', 'Tax Code', 'Taxable Amt', 'Tax Amt', 'Recoverable', 'Type', 'WHT'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtDate(row.transactionDate)}</td>
                  <td className="px-3 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold', MODULE_COLORS[row.sourceModule] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400')}>
                      {row.sourceModule}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-brand-700 dark:text-brand-400">{row.sourceDocumentNo}</td>
                  <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400">{row.transactionType}</td>
                  <td className="px-3 py-2.5 font-mono text-slate-700 dark:text-slate-300">{row.taxCodeId}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-800 dark:text-slate-200">{fmtAmt(row.taxableAmount)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-blue-700 dark:text-blue-400 font-semibold">{fmtAmt(row.taxAmount)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-green-700 dark:text-green-400">
                    {row.isRecoverable ? fmtAmt(row.taxAmount) : '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold',
                      row.isInputTax ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' : 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400')}>
                      {row.isInputTax ? 'Input' : 'Output'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {row.isWithholding && <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">WHT</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">Page {page} of {totalPages} · {total.toLocaleString()} records</p>
          <div className="flex items-center gap-1">
            <button onClick={() => run(page - 1)} disabled={page <= 1 || loading} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40"><ChevronLeft size={16} /></button>
            <button onClick={() => run(page + 1)} disabled={page >= totalPages || loading} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40"><ChevronRight size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
};
